import type { ReactNode } from 'react';

import { IconArrowRightLeft, IconCrossSmall } from '../../core/icons';

export type DiffCompareToggleProps = {
    readonly active: boolean;
    readonly onToggle: () => void;
};

export function DiffCompareToggle({ active, onToggle }: DiffCompareToggleProps) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-label={active ? 'Close compare' : 'Compare with current'}
            className="inline-flex shrink-0 items-center gap-xs rounded-full border border-gray-300 bg-gray-100 px-s py-xxs text-xxs font-medium text-black-600 transition-colors duration-200 ease hover:border-gray-400 hover:bg-gray-200"
        >
            {active ? (
                <IconCrossSmall size={12} aria-hidden={true} />
            ) : (
                <IconArrowRightLeft size={12} aria-hidden={true} />
            )}
            {active ? 'Close compare' : 'Compare'}
        </button>
    );
}

function DiffCompareColumn({
    label,
    empty = false,
    children,
}: {
    readonly label: string;
    readonly empty?: boolean;
    readonly children: ReactNode;
}) {
    return (
        <div className="flex min-w-0 flex-1 flex-col gap-xs">
            <span className="text-xxxs font-semibold uppercase tracking-wide text-black-400">{label}</span>
            <div
                className={empty ? 'text-sm italic leading-snug text-black-400' : 'text-sm leading-snug text-black-800'}
            >
                {children}
            </div>
        </div>
    );
}

export type DiffCompareColumnsProps = {
    readonly current: ReactNode;
    readonly proposed: ReactNode;
    readonly currentEmpty?: boolean;
    readonly currentLabel?: string;
    readonly proposedLabel?: string;
};

export function DiffCompareColumns({
    current,
    proposed,
    currentEmpty = false,
    currentLabel = 'Current',
    proposedLabel = 'Proposed',
}: DiffCompareColumnsProps) {
    return (
        <div className="flex min-w-0 gap-m">
            <DiffCompareColumn label={currentLabel} empty={currentEmpty}>
                {current}
            </DiffCompareColumn>
            <div className="w-px shrink-0 bg-gray-200" aria-hidden={true} />
            <DiffCompareColumn label={proposedLabel}>{proposed}</DiffCompareColumn>
        </div>
    );
}
